<template>
  <ModalDefault
    :id="modalId"
    :title="tabName ? `Cerrar alerta · ${tabName}` : 'Cerrar alerta'"
    size="lg"
    :button="false"
  >
    <template #modal-body>
      <div v-if="loading" class="py-10 text-center text-sm text-gray-500">
        Cargando formulario...
      </div>
      <div v-else-if="!topLevelComponents.length" class="py-10 text-center text-sm text-red-500">
        No se encontró el formulario configurado para esta alerta. Verifica la configuración de la automatización.
      </div>
      <div v-else class="max-h-[60vh] overflow-y-auto pr-2">
        <div class="grid grid-cols-12 gap-2">
          <ComponentForm
            v-for="element in topLevelComponents"
            :key="element.name"
            :comp="element"
            class="bg-white rounded-lg border my-2 dark:bg-slate-900"
          />
        </div>
      </div>
    </template>
    <template #modal-footer>
      <ButtonDefault color="gray" @click="closeModal(modalId)">Cancelar</ButtonDefault>
      <ButtonDefault color="blue" :disabled="loading || saving || !topLevelComponents.length" @click="handleGuardar">
        {{ saving ? 'Guardando...' : 'Guardar y cerrar alerta' }}
      </ButtonDefault>
    </template>
  </ModalDefault>
</template>

<script setup lang="ts">
import { ref, getCurrentInstance } from 'vue'
import { closeModal } from '@/modules/Core/usings/generales'
import ButtonDefault from '@/modules/Core/components/general/ButtonDefault.vue'
import ModalDefault from '@/modules/Core/components/Layout/ModalDefault.vue'
import { formularioServicios, alertasService } from '@/services'
import ComponentForm from '@/modules/Core/components/Form/ComponentForm.vue'
import { mapComponentsForm } from '@/modules/Core/usings/generales'
import { getFieldsFromTabs } from '@/modules/Configuracion/usings/usingFormularios'
import type { IFormComponent } from '@/modules/Configuracion/interfaces/IFormComponent'
import type { IConfComponents, IConfFormxtab } from '@/modules/Administracion/interfaces'
import type { ITabxcomponents } from '@/modules/Configuracion/interfaces/IFormComponent'

const LAYOUT_TYPES = ['SECTION', 'CONTAINER', 'TITLE']

const props = defineProps<{
  alerId: number
  codoId: number
  cittId: number
}>()

const emit = defineEmits<{
  closed: []
}>()

// uid único por instancia — evita colisión de id en el DOM cuando hay múltiples
// BtnCerrarAlerta (AlertsMini + AlertsFull) montados a la vez sobre la misma alerta.
const uid = getCurrentInstance()?.uid ?? Math.floor(Math.random() * 1e9)
const modalId = `modal-cierre-formulario-${props.alerId}-${uid}`

const loading = ref(false)
const saving = ref(false)
const topLevelComponents = ref<IFormComponent[]>([])
const tabName = ref<string>('')

const isLayoutType = (type?: string) => !!type && LAYOUT_TYPES.includes(type)

const collectLeaves = (
  list: IFormComponent[] = [],
  out: IFormComponent[] = []
): IFormComponent[] => {
  for (const c of list) {
    if (isLayoutType(c.typeComponent)) {
      collectLeaves((c.components || []) as IFormComponent[], out)
    } else {
      out.push(c)
    }
  }
  return out
}

const cargarFormulario = async () => {
  loading.value = true
  topLevelComponents.value = []
  tabName.value = ''
  try {
    if (!props.codoId) {
      loading.value = false
      return
    }
    const { data } = await formularioServicios.view(props.codoId, {
      withComponents: true,
      edit: true
    })
    const formulario = (data?.body || {}) as { tabs?: IConfFormxtab[] }
    const firstTab = formulario.tabs?.[0]
    tabName.value = firstTab?.cftaNombre || ''
    const tabs = (firstTab ? [firstTab] : []).filter(e => e.components)
    const { camposComponent } = getFieldsFromTabs<IConfFormxtab, ITabxcomponents>(tabs, 'cftaNombre')
    topLevelComponents.value = camposComponent
      .filter(e => e.typeComponent === 'SECTION' && !e.componentId)
      .map(item => mapComponentsForm(item as IConfComponents, camposComponent))
  } catch (err) {
    console.error('Error cargando formulario de cierre:', err)
    window.notificacion?.notificacion('failure', 'Error al cargar el formulario de cierre')
  } finally {
    loading.value = false
  }
}

defineExpose({ cargarFormulario, modalId })

const handleGuardar = async () => {
  if (!props.cittId) {
    window.notificacion?.notificacion('failure', 'Falta el contexto de la atención para guardar')
    return
  }
  saving.value = true
  window.notificacion?.loading().circle('Guardando...')
  try {
    const leaves = collectLeaves(topLevelComponents.value)
    const formData = leaves.map(c => ({
      name: c.name,
      label: c.label,
      typeComponent: c.typeComponent,
      value: String(c.value ?? ''),
      searchValue: String((c as { searchValue?: unknown }).searchValue ?? c.value ?? '')
    }))

    const res = await alertasService.cierreFormulario(props.alerId, {
      cittId: props.cittId,
      formData
    })

    window.notificacion?.loading().remove()

    if (res?.data?.warning) return

    window.notificacion?.notificacion('success', 'Alerta cerrada correctamente')
    closeModal(modalId)
    emit('closed')
  } catch (err) {
    console.error('Error guardando cierre de alerta:', err)
    window.notificacion?.loading().remove()
    window.notificacion?.notificacion('failure', 'Error al guardar el cierre de la alerta')
  } finally {
    saving.value = false
  }
}

</script>
